/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { FlightRule, FlightType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface FlightFollowingReportProps {
  id: string;
  summary: string | null;
  fuel: string | null;
  estimatedTimeEnrouteMinutes: number | null;
  riskAnalysis: string | null;
  flightType: FlightType | null;
  soulsOnBoard: number | null;
  highestFeatureEnroute: number | null;
  takeOffWeight: number | null;
  centerOfGravity: number | null;
  destination: string | null;
  trackingViaGps: boolean | null;
  flightRule: FlightRule | null;
  tripLegId: string | null;
  confirmedFboId: string | null;
  cruisingAltitude: number | null;
  medicalCrewRA: number | null;
  tripId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class FlightFollowingReportRecord extends Record<FlightFollowingReportProps>({
  id: null as any,
  summary: null as any,
  fuel: null as any,
  estimatedTimeEnrouteMinutes: null as any,
  riskAnalysis: null as any,
  flightType: null as any,
  soulsOnBoard: null as any,
  highestFeatureEnroute: null as any,
  takeOffWeight: null as any,
  centerOfGravity: null as any,
  destination: null as any,
  trackingViaGps: null as any,
  flightRule: null as any,
  tripLegId: null as any,
  confirmedFboId: null as any,
  cruisingAltitude: null as any,
  medicalCrewRA: null as any,
  tripId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    summary: string | null;
    fuel: string | null;
    estimatedTimeEnrouteMinutes: number | null;
    riskAnalysis: string | null;
    flightType: FlightType | null;
    soulsOnBoard: number | null;
    highestFeatureEnroute: number | null;
    takeOffWeight: number | null;
    centerOfGravity: number | null;
    destination: string | null;
    trackingViaGps: boolean | null;
    flightRule: FlightRule | null;
    tripLegId: string | null;
    confirmedFboId: string | null;
    cruisingAltitude: number | null;
    medicalCrewRA: number | null;
    tripId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): FlightFollowingReportRecord {
    if (json == null) {
      throw new Error('FlightFollowingReportRecord.fromJS: parameter cannot be null');
    }
    return new FlightFollowingReportRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'FlightFollowingReportJson.id',
      ),
      summary: json.summary,
      fuel: json.fuel,
      estimatedTimeEnrouteMinutes: json.estimatedTimeEnrouteMinutes,
      riskAnalysis: json.riskAnalysis,
      flightType: json.flightType,
      soulsOnBoard: json.soulsOnBoard,
      highestFeatureEnroute: json.highestFeatureEnroute,
      takeOffWeight: json.takeOffWeight,
      centerOfGravity: json.centerOfGravity,
      destination: json.destination,
      trackingViaGps: json.trackingViaGps,
      flightRule: json.flightRule,
      tripLegId: json.tripLegId,
      confirmedFboId: json.confirmedFboId,
      cruisingAltitude: json.cruisingAltitude,
      medicalCrewRA: json.medicalCrewRA,
      tripId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'tripId', '00000000-0000-0000-0000-000000000000'),
        'FlightFollowingReportJson.tripId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'FlightFollowingReportJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    summary: string | null;
    fuel: string | null;
    estimatedTimeEnrouteMinutes: number | null;
    riskAnalysis: string | null;
    flightType: FlightType | null;
    soulsOnBoard: number | null;
    highestFeatureEnroute: number | null;
    takeOffWeight: number | null;
    centerOfGravity: number | null;
    destination: string | null;
    trackingViaGps: boolean | null;
    flightRule: FlightRule | null;
    tripLegId: string | null;
    confirmedFboId: string | null;
    cruisingAltitude: number | null;
    medicalCrewRA: number | null;
    tripId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [FlightFollowingReportRecord, Error[]] {
    if (json == null) {
      throw new Error('FlightFollowingReportRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new FlightFollowingReportRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'FlightFollowingReportJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      summary: tryOrDefault(
        () => json.summary,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fuel: tryOrDefault(
        () => json.fuel,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      estimatedTimeEnrouteMinutes: tryOrDefault(
        () => json.estimatedTimeEnrouteMinutes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      riskAnalysis: tryOrDefault(
        () => json.riskAnalysis,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      flightType: tryOrDefault(
        () => json.flightType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      soulsOnBoard: tryOrDefault(
        () => json.soulsOnBoard,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      highestFeatureEnroute: tryOrDefault(
        () => json.highestFeatureEnroute,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      takeOffWeight: tryOrDefault(
        () => json.takeOffWeight,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      centerOfGravity: tryOrDefault(
        () => json.centerOfGravity,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      destination: tryOrDefault(
        () => json.destination,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      trackingViaGps: tryOrDefault(
        () => json.trackingViaGps,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      flightRule: tryOrDefault(
        () => json.flightRule,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      tripLegId: tryOrDefault(
        () => json.tripLegId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      confirmedFboId: tryOrDefault(
        () => json.confirmedFboId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cruisingAltitude: tryOrDefault(
        () => json.cruisingAltitude,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      medicalCrewRA: tryOrDefault(
        () => json.medicalCrewRA,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      tripId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'tripId', '00000000-0000-0000-0000-000000000000'),
            'FlightFollowingReportJson.tripId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'FlightFollowingReportJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: FlightFollowingReportProps) {
    super(props);
    if (props == null) {
      throw new Error('FlightFollowingReportRecord constructor parameter cannot be null');
    }
  }
}

export abstract class FlightFollowingReportViewModelBase implements FlightFollowingReportProps {
  protected readonly _props: FlightFollowingReportProps;
  private _json?: FlightFollowingReportProps;

  get id(): string {
    return this._props.id;
  }
  get summary(): string | null {
    return this._props.summary;
  }
  get fuel(): string | null {
    return this._props.fuel;
  }
  get estimatedTimeEnrouteMinutes(): number | null {
    return this._props.estimatedTimeEnrouteMinutes;
  }
  get riskAnalysis(): string | null {
    return this._props.riskAnalysis;
  }
  get flightType(): FlightType | null {
    return this._props.flightType;
  }
  get soulsOnBoard(): number | null {
    return this._props.soulsOnBoard;
  }
  get highestFeatureEnroute(): number | null {
    return this._props.highestFeatureEnroute;
  }
  get takeOffWeight(): number | null {
    return this._props.takeOffWeight;
  }
  get centerOfGravity(): number | null {
    return this._props.centerOfGravity;
  }
  get destination(): string | null {
    return this._props.destination;
  }
  get trackingViaGps(): boolean | null {
    return this._props.trackingViaGps;
  }
  get flightRule(): FlightRule | null {
    return this._props.flightRule;
  }
  get tripLegId(): string | null {
    return this._props.tripLegId;
  }
  get confirmedFboId(): string | null {
    return this._props.confirmedFboId;
  }
  get cruisingAltitude(): number | null {
    return this._props.cruisingAltitude;
  }
  get medicalCrewRA(): number | null {
    return this._props.medicalCrewRA;
  }
  get tripId(): string {
    return this._props.tripId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isPt133() {
    return this._props.flightType === FlightType.Pt133;
  }
  public get isPt135() {
    return this._props.flightType === FlightType.Pt135;
  }
  public get isPt91() {
    return this._props.flightType === FlightType.Pt91;
  }
  public get isIfr() {
    return this._props.flightRule === FlightRule.Ifr;
  }
  public get isNone() {
    return this._props.flightRule === FlightRule.None;
  }
  public get isVfr() {
    return this._props.flightRule === FlightRule.Vfr;
  }

  public isAnyFlightType(...flightType: FlightType[]) {
    return this._props.flightType != null && flightType.includes(this._props.flightType);
  }
  public isAnyFlightRule(...flightRule: FlightRule[]) {
    return this._props.flightRule != null && flightRule.includes(this._props.flightRule);
  }

  constructor(props: FlightFollowingReportProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        summary: this._props.summary,
        fuel: this._props.fuel,
        estimatedTimeEnrouteMinutes: this._props.estimatedTimeEnrouteMinutes,
        riskAnalysis: this._props.riskAnalysis,
        flightType: this._props.flightType,
        soulsOnBoard: this._props.soulsOnBoard,
        highestFeatureEnroute: this._props.highestFeatureEnroute,
        takeOffWeight: this._props.takeOffWeight,
        centerOfGravity: this._props.centerOfGravity,
        destination: this._props.destination,
        trackingViaGps: this._props.trackingViaGps,
        flightRule: this._props.flightRule,
        tripLegId: this._props.tripLegId,
        confirmedFboId: this._props.confirmedFboId,
        cruisingAltitude: this._props.cruisingAltitude,
        medicalCrewRA: this._props.medicalCrewRA,
        tripId: this._props.tripId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllFlightFollowingReportEntities = (state: AppState) =>
  state.entityStores.flightFollowingReport.entities;

export const selectFlightFollowingReportById = (id: string) => (state: AppState) =>
  state.entityStores.flightFollowingReport.entities.get(id);

export const selectFlightFollowingReportMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.flightFollowingReport.meta.get(id);

export const selectFlightFollowingReportAndMetadataById = (id: string) =>
  createSelector(
    selectFlightFollowingReportById(id),
    selectFlightFollowingReportMetadataById(id),
    (flightFollowingReport, meta) => ({ flightFollowingReport, meta }),
  );

export function selectFlightFollowingReportEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllFlightFollowingReportEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, FlightFollowingReportRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectFlightFollowingReportEntitiesBySummary(summary: string) {
  return createSelector(selectAllFlightFollowingReportEntities, (entities) =>
    entities.filter((e) => e.summary === summary).valueSeq(),
  );
}

export function selectFlightFollowingReportEntitiesByFuel(fuel: string) {
  return createSelector(selectAllFlightFollowingReportEntities, (entities) =>
    entities.filter((e) => e.fuel === fuel).valueSeq(),
  );
}

export function selectFlightFollowingReportEntitiesByEstimatedTimeEnrouteMinutes(
  estimatedTimeEnrouteMinutes: number,
) {
  return createSelector(selectAllFlightFollowingReportEntities, (entities) =>
    entities
      .filter((e) => e.estimatedTimeEnrouteMinutes === estimatedTimeEnrouteMinutes)
      .valueSeq(),
  );
}

export function selectFlightFollowingReportEntitiesByRiskAnalysis(riskAnalysis: string) {
  return createSelector(selectAllFlightFollowingReportEntities, (entities) =>
    entities.filter((e) => e.riskAnalysis === riskAnalysis).valueSeq(),
  );
}

export function selectFlightFollowingReportEntitiesByFlightType(flightType: FlightType) {
  return createSelector(selectAllFlightFollowingReportEntities, (entities) =>
    entities.filter((e) => e.flightType === flightType).valueSeq(),
  );
}

export function selectFlightFollowingReportEntitiesBySoulsOnBoard(soulsOnBoard: number) {
  return createSelector(selectAllFlightFollowingReportEntities, (entities) =>
    entities.filter((e) => e.soulsOnBoard === soulsOnBoard).valueSeq(),
  );
}

export function selectFlightFollowingReportEntitiesByHighestFeatureEnroute(
  highestFeatureEnroute: number,
) {
  return createSelector(selectAllFlightFollowingReportEntities, (entities) =>
    entities.filter((e) => e.highestFeatureEnroute === highestFeatureEnroute).valueSeq(),
  );
}

export function selectFlightFollowingReportEntitiesByTakeOffWeight(takeOffWeight: number) {
  return createSelector(selectAllFlightFollowingReportEntities, (entities) =>
    entities.filter((e) => e.takeOffWeight === takeOffWeight).valueSeq(),
  );
}

export function selectFlightFollowingReportEntitiesByCenterOfGravity(centerOfGravity: number) {
  return createSelector(selectAllFlightFollowingReportEntities, (entities) =>
    entities.filter((e) => e.centerOfGravity === centerOfGravity).valueSeq(),
  );
}

export function selectFlightFollowingReportEntitiesByDestination(destination: string) {
  return createSelector(selectAllFlightFollowingReportEntities, (entities) =>
    entities.filter((e) => e.destination === destination).valueSeq(),
  );
}

export function selectFlightFollowingReportEntitiesByTrackingViaGps(trackingViaGps: boolean) {
  return createSelector(selectAllFlightFollowingReportEntities, (entities) =>
    entities.filter((e) => e.trackingViaGps === trackingViaGps).valueSeq(),
  );
}

export function selectFlightFollowingReportEntitiesByFlightRule(flightRule: FlightRule) {
  return createSelector(selectAllFlightFollowingReportEntities, (entities) =>
    entities.filter((e) => e.flightRule === flightRule).valueSeq(),
  );
}

export function selectFlightFollowingReportEntitiesByTripLegId(tripLegId: string) {
  return createSelector(selectAllFlightFollowingReportEntities, (entities) =>
    entities.filter((e) => e.tripLegId === tripLegId).valueSeq(),
  );
}

export function selectFlightFollowingReportEntitiesByConfirmedFboId(confirmedFboId: string) {
  return createSelector(selectAllFlightFollowingReportEntities, (entities) =>
    entities.filter((e) => e.confirmedFboId === confirmedFboId).valueSeq(),
  );
}

export function selectFlightFollowingReportEntitiesByCruisingAltitude(cruisingAltitude: number) {
  return createSelector(selectAllFlightFollowingReportEntities, (entities) =>
    entities.filter((e) => e.cruisingAltitude === cruisingAltitude).valueSeq(),
  );
}

export function selectFlightFollowingReportEntitiesByMedicalCrewRA(medicalCrewRA: number) {
  return createSelector(selectAllFlightFollowingReportEntities, (entities) =>
    entities.filter((e) => e.medicalCrewRA === medicalCrewRA).valueSeq(),
  );
}

export function selectFlightFollowingReportEntitiesByTripId(tripId: string) {
  return createSelector(selectAllFlightFollowingReportEntities, (entities) =>
    entities.filter((e) => e.tripId === tripId).valueSeq(),
  );
}

export function selectFlightFollowingReportEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllFlightFollowingReportEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
